import React, { useEffect, useState } from 'react';

import { Inline, Text } from '@atlaskit/primitives/compiled';

import TableTree, { Cell, Header, Headers, Row, Rows } from '@atlaskit/table-tree';

import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';

import {
	JiraIcon,
	ConfluenceDataCenterIcon,
	JiraDataCenterIcon,
	ConfluenceIcon,
} from '@atlaskit/logo';

import { IconButton } from '@atlaskit/button/new';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import type { ApplicationLinkV3Data, ApplicationLinkV3Status } from '../../common/types.tsx';

type TypeDefinition = {
	name: string;
	icon: React.ReactElement;
};

const TypeIdMap: Record<string, TypeDefinition> = {
	jira: {
		name: 'Jira',
		icon: <JiraIcon shouldUseNewLogoDesign label="Jira" />,
	},
	'jira-dc': {
		name: 'Jira DC',
		icon: <JiraDataCenterIcon shouldUseNewLogoDesign label="Jira Data Center" />,
	},
	confluence: {
		name: 'Confluence',
		icon: <ConfluenceIcon shouldUseNewLogoDesign label="Confluence" />,
	},
	'confluence-dc': {
		name: 'Confluence DC',
		icon: <ConfluenceDataCenterIcon shouldUseNewLogoDesign label="Confluence Data Center" />,
	},
};

const lookupType = (link: ApplicationLinkV3Data): TypeDefinition => {
	let type = link.type;
	// TODO: This is inverted (oops!)
	if (link.data.applicationVersion !== undefined) {
		type += '-dc';
	}

	const typeDef = TypeIdMap[type] || {
		name: 'Unknown',
		icon: <Text>Placeholder</Text>,
	};

	return typeDef;
};

const ApplicationLinkName = (props: ApplicationLinkV3Data) => {
	const appendLozenges = (isPrimary: boolean, isSystem: boolean, isExtraAuthRequired: boolean) => {
		return (
			<>
				{isPrimary && <Lozenge key="primary">Primary</Lozenge>}
				{isSystem && <Lozenge key="system">System</Lozenge>}
				{isExtraAuthRequired && <Lozenge key="extra-auth">Extra Auth</Lozenge>}
			</>
		);
	};

	const typeDef = lookupType(props);

	return (
		<>
			<Inline alignBlock="center" space="space.100">
				{typeDef.icon}
				{props.name}
				{appendLozenges(props.primary, props.system, props.isExtraAuthRequired)}
			</Inline>
		</>
	);
};

const ApplicationLinkVersion = (props: ApplicationLinkV3Data) => {
	const typeDef = lookupType(props);
	const name = props.data.applicationVersion
		? `${typeDef.name} (${props.data.applicationVersion})`
		: typeDef.name;

	return (
		<>
			<Text>{name}</Text>
		</>
	);
};

const ApplicationLinkType = (props: ApplicationLinkV3Data) => {
	return (
		<>
			<Text>{props.name}</Text>
		</>
	);
};

const ApplicationLinkStatus = (props: ApplicationLinkV3Data) => {
	const [applinkStatus, setApplinkStatus] = useState<ApplicationLinkV3Status | null>(null);

	useEffect(() => {
		setApplinkStatus(null);
		// TODO: Properly mock data
		// fetchApplinkStatus(props.id)
		// 	.then((status: ApplicationLinkV3Status) => {
		// 		setApplinkStatus(status);
		// 	})
		// 	.catch((err: Error) => {
		// 		log.safeErrorWithoutCustomerData(LOG_LOCATION, 'Failed to fetch applink status', err);
		// 	});
	}, [props.id]);

	// TODO: Pop-ups
	const createLozenge = (contents: ApplicationLinkV3Status) => {
		// First, check if the link is working
		if (contents.working) {
			return <Lozenge appearance="success">Connected</Lozenge>;
		}

		// Next, check if there is an error
		if (contents.error) {
			return <Lozenge appearance="removed">Config Error</Lozenge>;
		}

		// TODO: Others
		return <Lozenge>Unknown</Lozenge>;
	};

	return applinkStatus ? createLozenge(applinkStatus) : <Spinner />;
};

const ApplicationLinkPopupMenu = (props: ApplicationLinkV3Data) => {
	return (
		<>
			<IconButton
				// {...triggerProps}
				isSelected={false}
				onClick={() => {
					// unused
					// eslint-disable-next-line no-console
					console.log(props.data.configUrl);
				}}
				icon={ShowMoreHorizontalIcon}
				label="More"
			/>
		</>
	);
};

type Item = {
	id: string;
	content: ApplicationLinkV3Data;
	hasChildren: boolean;
	children?: Item[];
};

const convertToArray = (applinks: ApplicationLinkV3Data[]): Item[] => {
	const wrapWithItem = (
		id: string,
		content: ApplicationLinkV3Data,
		hasChildren: boolean,
		children?: Item[],
	) => {
		return { id, content, hasChildren, children };
	};

	return applinks.map((entry, index) => {
		return wrapWithItem(`entry-${index}`, entry, false);
	});
};

const ApplinksTable = (props: { applinks: ApplicationLinkV3Data[] | null }) => {
	const applinkItems = props.applinks ? convertToArray(props.applinks) : undefined;

	return (
		<TableTree label="Application Links">
			<Headers>
				<Header width="30%">Application</Header>
				<Header width="30%">Version</Header>
				<Header width="20%">Link type</Header>
				<Header width="15%">Status</Header>
				<Header width="5%" />
			</Headers>
			<Rows
				items={applinkItems}
				render={(item: Item) => {
					const { content } = item;

					const row = (
						<Row key={item.id} itemId={item.id} hasChildren={item.hasChildren}>
							<Cell>
								<ApplicationLinkName {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkVersion {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkType {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkStatus {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkPopupMenu {...content} />
							</Cell>
						</Row>
					);

					return row;
				}}
			/>
		</TableTree>
	);
};

export default ApplinksTable;
